// src/Redux/thunks/doctorProfileThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosDoctorProfile } from "../../../api/axios";
import { DOCTOR_PROFILE_PATHS } from "../../../api/endpoints";

const errPayload = (e, fallback) => e?.response?.data || { message: fallback };

const isNotFound = (e) => {
  const status = e?.response?.status;
  const msg = e?.response?.data?.message;
  return status === 404 || String(msg || "").toLowerCase().includes("not found");
};

/* ===================== PING ===================== */
export const pingDoctorProfiles = createAsyncThunk(
  "doctorProfile/ping",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.get(DOCTOR_PROFILE_PATHS.PING);
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Ping doctor-profiles failed"));
    }
  }
);

/* ===================== GET by doctorId ===================== */
export const fetchDoctorInfoByDoctorId = createAsyncThunk(
  "doctorProfile/fetchDoctorInfoByDoctorId",
  async (doctorId, { rejectWithValue, getState }) => {
    const isUUID = (value) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
        String(value).toLowerCase()
      );

    const toSafeInt = (value) => {
      if (value == null) return null;
      const n = Number(String(value).trim());
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    const resolveStateDoctorId = () => {
      const state = getState?.() || {};
      const candidates = [
        state?.doctor?.user?.pgId,
        state?.doctor?.user?.doctorId,
        state?.doctor?.user?.id,
        state?.doctors?.currentDoctor?.pgId,
        state?.doctors?.currentDoctor?.doctorId,
        state?.doctors?.currentDoctor?.id,
      ];

      for (const candidate of candidates) {
        const parsed = toSafeInt(candidate);
        if (parsed != null) return parsed;
      }

      return null;
    };

    try {
      const { data } = await axiosDoctorProfile.get(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.GET_BY_DOCTOR_ID(doctorId)
      );
      return data;
    } catch (e) {
      if (isNotFound(e) && isUUID(doctorId)) {
        const fallbackDoctorId = resolveStateDoctorId();

        if (fallbackDoctorId != null) {
          try {
            const { data } = await axiosDoctorProfile.get(
              DOCTOR_PROFILE_PATHS.DOCTOR_INFO.GET_BY_DOCTOR_ID(fallbackDoctorId)
            );
            return data;
          } catch (fallbackError) {
            if (isNotFound(fallbackError)) return null;
            return rejectWithValue(errPayload(fallbackError, "Fetch doctor info failed"));
          }
        }
      }

      // ✅ 404 => profil vide (pas une vraie erreur UI)
      if (isNotFound(e)) return null;
      return rejectWithValue(errPayload(e, "Fetch doctor info failed"));
    }
  }
);

/* ===================== CREATE ===================== */
// ✅ AJOUTÉ — manquait dans le fichier original, causait le SyntaxError
export const createDoctorInfo = createAsyncThunk(
  "doctorProfile/createDoctorInfo",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.post(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.CREATE,
        payload
      );
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Create doctor info failed"));
    }
  }
);

/* ===================== UPDATE by doctorId ===================== */
export const updateDoctorInfoByDoctorId = createAsyncThunk(
  "doctorProfile/updateDoctorInfoByDoctorId",
  async ({ doctorId, patch }, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.patch(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.UPDATE_BY_DOCTOR_ID(doctorId),
        patch
      );
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Update doctor info failed"));
    }
  }
);

/* ===================== DELETE by doctorId ===================== */
export const deleteDoctorInfoByDoctorId = createAsyncThunk(
  "doctorProfile/deleteDoctorInfoByDoctorId",
  async (doctorId, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.delete(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.DELETE_BY_DOCTOR_ID(doctorId)
      );
      return { doctorId, data };
    } catch (e) {
      return rejectWithValue(errPayload(e, "Delete doctor info failed"));
    }
  }
);

/* ===================== UPSERT ===================== */
export const upsertDoctorInfo = createAsyncThunk(
  "doctorProfile/upsertDoctorInfo",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.post(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.UPSERT,
        payload
      );
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Upsert doctor info failed"));
    }
  }
);

/* ===================== GET by id ===================== */
export const fetchDoctorInfoById = createAsyncThunk(
  "doctorProfile/fetchDoctorInfoById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.get(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.GET_BY_ID(id)
      );
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Fetch doctor info by id failed"));
    }
  }
);

/* ===================== PATCH by id ===================== */
export const updateDoctorInfoById = createAsyncThunk(
  "doctorProfile/updateDoctorInfoById",
  async ({ id, patch }, { rejectWithValue }) => {
    try {
      const { data } = await axiosDoctorProfile.patch(
        DOCTOR_PROFILE_PATHS.DOCTOR_INFO.UPDATE_BY_ID(id),
        patch
      );
      return data;
    } catch (e) {
      return rejectWithValue(errPayload(e, "Update doctor info by id failed"));
    }
  }
);